/**
 * Home — the guide, as a workspace rather than a page.
 *
 * The first version was three cards and a search box, which told a new reader
 * that three of a hundred and thirty-seven functions existed. The second put
 * all of them on the page and was read back as correct but unlovely: one type
 * size, one grey, no anchor for the eye, cards that were rectangles with text
 * in the top-left corner. Information architecture is not design.
 *
 * So the content stays and the surface is rebuilt around four ideas:
 *
 *   **A masthead, not a heading.** The product has a Bodoni wordmark that
 *   nothing was using; it belongs at the front, with the contour motif and a
 *   rail of real numbers under it. A page that opens on flat text is a
 *   settings page.
 *
 *   **Colour as a label.** Diffusion blue, perfusion warm, and cool hues for
 *   the sections that cut across both — carried by a rule, a dot or a tint,
 *   never a filled panel. See ./home.design.tsx.
 *
 *   **A scale worth the name.** 34 / 15 / 12.5 / 11 with real weight
 *   differences, instead of four sizes a point apart doing the work of one.
 *
 *   **Sequence drawn as sequence.** The five steps and the pipeline stages are
 *   ordered things, so they are drawn on a rail with numbered nodes. A table
 *   of five equal boxes says they are alternatives.
 *
 * The third version fixed the last thing wrong with it: length. Everything
 * above was true and the page was four and a half thousand pixels tall, which
 * is a web page. An application does not ask you to scroll past the method
 * section to reach your own studies — its places sit side by side and each one
 * fits the window. So the sections became **places** on a rail, in the order
 * the questions arrive:
 *
 *   Start · Studies · Output · Method · Reference
 *
 * Only Reference is long, because a reference is, and even that is split by
 * section so each is about a screen. The masthead shrank to a header strip:
 * it identifies the instrument and shows the engine, which is all it was ever
 * for, and it no longer costs a third of the first screen to do it.
 */

import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Clock,
  FlaskConical,
  FolderOpen,
  Layers,
  Play,
  Plus,
  Search,
  Table2,
} from "lucide-react";

import { Button, Input } from "@/components/ui/primitives";
import { listJobs, listRecentCases, openCase, splitError } from "@/ipc/api";
import type { PipelineKind, RecentCase } from "@/ipc/contract";
import { cn, formatDateTime, formatDuration } from "@/lib/utils";
import {
  loadExampleAndOpenSubject,
  pickAndOpenCohort,
  pickAndOpenSubject,
  showCaseInViewer,
} from "@/pipeline/actions";
import type { RunScope } from "@/usecases/actions";
import { describeFacts, pipelineFacts } from "@/pipeline/stages";
import { dispatchAction } from "@/shell/actions";
import { useAppStore } from "@/store/appStore";
import { USE_CASES, type UseCase } from "@/usecases/catalog";
import { startUseCase, useCaseIssue } from "@/usecases/actions";
import { accentFor, accentVars, ContourMotif, Eyebrow, SURFACE } from "./home.design";
import { buildGuide, searchGuide } from "./guide";
import { DatasetTab } from "@/dataset/DatasetTab";
import { NewProject } from "@/projects/NewProject";
import { ProjectPage } from "@/projects/ProjectPage";
import { useProjectDraft, useProjects } from "@/projects/store";
import { useNavStore } from "./navStore";
import { useWorkspaces } from "./workspaces";

/** Which accent an analysis card wears. Keyed by use case, not by section. */
const ANALYSIS_ACCENT: Record<string, string> = {
  "read.dwi": "dwi",
  "read.pwi": "perfusion",
  "read.mismatch": "quantification",
};

/** Which pipeline each card actually runs, for the counts and the timings. */
const ANALYSIS_KIND: Record<string, PipelineKind> = {
  "read.dwi": "dwi",
  "read.pwi": "pwi",
  "read.mismatch": "combined",
};

const KIND_TAG: Record<PipelineKind, string> = {
  dwi: "DWI",
  pwi: "PWI",
  combined: "DWI + PWI",
};

/**
 * How long this pipeline last took here, or null.
 *
 * The job registry is in memory, so this is empty until something has run in
 * this session — and then it is a measurement rather than an estimate, which
 * is the only kind of number worth putting on this page.
 */
function useLastRunSeconds(kind: PipelineKind | undefined): number | null {
  const [seconds, setSeconds] = useState<number | null>(null);
  useEffect(() => {
    if (!kind) return;
    let live = true;
    void listJobs(undefined, 40)
      .then((jobs) => {
        if (!live) return;
        const match = jobs.find(
          (j) =>
            j.state === "completed" &&
            j.startedIso &&
            j.finishedIso &&
            j.stages.length === pipelineFacts(kind).stages,
        );
        if (!match?.startedIso || !match.finishedIso) return;
        const ms = new Date(match.finishedIso).getTime() - new Date(match.startedIso).getTime();
        setSeconds(ms > 0 ? ms / 1000 : null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [kind]);
  return seconds;
}

/* ------------------------------------------------------------- headings --- */

function SectionHeading({
  id,
  eyebrow,
  title,
  blurb,
  aside,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  blurb?: string;
  aside?: React.ReactNode;
}) {
  return (
    <div id={id} className="mb-4 flex items-end justify-between gap-6 scroll-mt-4">
      <div className="min-w-0">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-1.5 text-[19px] font-semibold leading-tight tracking-[-0.01em] text-neutral-900">
          {title}
        </h2>
        {blurb ? (
          <p className="mt-1.5 max-w-2xl text-[12.5px] leading-relaxed text-neutral-500">{blurb}</p>
        ) : null}
      </div>
      {aside ? <div className="shrink-0 pb-0.5">{aside}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------------- masthead -- */

function EnginePill() {
  const engine = useAppStore((s) => s.systemStatus?.engine);
  if (!engine) return null;
  const tone = engine.ready
    ? engine.deviceClass === "emulated"
      ? { dot: "bg-amber-500", ring: "ring-amber-200/70" }
      : { dot: "bg-emerald-500", ring: "ring-emerald-200/70" }
    : { dot: "bg-red-500", ring: "ring-red-200/70" };
  return (
    <button
      type="button"
      onClick={() => dispatchAction("dev.engine.setup")}
      title={engine.error ?? engine.deviceLabel}
      className="group flex shrink-0 items-center gap-2 rounded-full border border-ads-border bg-ads-surface/80 py-1.5 pl-2.5 pr-3 text-[11px] text-neutral-600 backdrop-blur transition hover:border-ads-border-strong hover:text-neutral-900"
    >
      <span className={cn("h-1.5 w-1.5 rounded-full ring-4", tone.dot, tone.ring)} />
      <span className="font-medium">
        {engine.ready ? engine.deviceLabel.split("—")[0].trim() : "Engine not set up"}
      </span>
      <ArrowUpRight className="h-3 w-3 text-neutral-300 transition group-hover:text-neutral-500" />
    </button>
  );
}

function HeaderStrip({
  query,
  setQuery,
}: {
  query: string;
  setQuery: (v: string) => void;
}) {
  // No wordmark here. The title bar carries it three centimetres above, and a
  // product that introduces itself twice on one screen has not decided where
  // its identity lives. This strip is a tool bar: find something, and see what
  // will run it.
  return (
    <div className="flex shrink-0 items-center gap-4 border-b border-ads-border bg-ads-surface px-7 py-3">
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          className="h-9.5 rounded-xl pl-10 text-[13px]"
          placeholder={`Search functions \u2014 try \u201cmismatch\u201d, \u201cresample\u201d, \u201cASPECTS\u201d`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <EnginePill />
    </div>
  );
}

/* --------------------------------------------------------- introduction --- */

/**
 * What the software is. It belongs on the first screen you land on rather than
 * in the chrome: the chrome is for things that stay true while you work, and
 * an introduction is read once.
 */
function Intro() {
  return (
    <div className={cn(SURFACE, "relative mb-6 max-w-3xl overflow-hidden")}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 150% at 90% 0%, rgba(37,99,235,0.07) 0%, rgba(37,99,235,0.02) 38%, transparent 68%)",
        }}
      />
      <ContourMotif className="absolute -right-12 -top-14 h-[230px] w-[230px]" />
      <div className="relative px-7 py-6">
        <h1 className="max-w-lg text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-neutral-900">
          Acute stroke detection and segmentation{" "}
          <span className="font-normal text-neutral-400">(ADS)</span>
        </h1>
        <p className="mt-3 max-w-lg text-[12.5px] leading-[1.6] text-neutral-600">
          ADS Desk runs the OpenADS diffusion and perfusion pipelines on a study you open from
          disk — lesion core, hypoperfusion, the mismatch between them, atlas quantification and
          the radiological report.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ analyses --- */

/**
 * One of the three analyses, described the way an instrument describes itself.
 *
 * This used to be a pastel icon tile above a marketing sentence, on a card
 * with a saturated bar across the top — the vocabulary of an app store, which
 * is what made the page read as a toy. The content was the other half of the
 * problem: "the full DWI pipeline" says nothing a reader deciding between
 * three options can weigh.
 *
 * So: colour is a hairline down the left edge, identifying the modality and
 * nothing else; the sentence is replaced by the size of the thing, counted
 * from the stage table; and the last run's duration appears when there was
 * one, because a tool that knows how long its own work takes is the thing
 * professional software has and a brochure does not.
 */
function AnalysisCard({ u, scope = "one" }: { u: UseCase; scope?: RunScope }) {
  const hasCase = useAppStore((s) => s.currentCase != null);
  const engineReady = useAppStore((s) => s.systemStatus?.engine.ready === true);
  const issue = useCaseIssue(u, hasCase, engineReady);
  // "Open a study first" is true of all three at once, so the heading says it
  // and the cards do not repeat it three times. Anything else — an engine that
  // is not set up, a capability that is not built — is specific to the card
  // and stays on it.
  // In many-subject mode there is no open study yet and there does not need to
  // be: the folder is chosen when the run starts.
  const cardIssue =
    scope === "many" && issue?.startsWith("Open a study")
      ? null
      : !hasCase && issue?.startsWith("Open a study")
        ? null
        : issue;
  const accent = accentFor(ANALYSIS_ACCENT[u.id] ?? "");
  const kind = ANALYSIS_KIND[u.id];
  const facts = kind ? pipelineFacts(kind) : null;
  const lastRun = useLastRunSeconds(kind);

  return (
    <button
      type="button"
      onClick={() => startUseCase(u, { scope })}
      style={accentVars(accent)}
      className={cn(
        "group relative flex flex-1 flex-col overflow-hidden rounded-md border border-ads-border bg-ads-surface text-left transition",
        "hover:border-ads-border-strong hover:bg-ads-subtle",
      )}
    >
      {/* The modality, as a rule rather than a badge. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[2px]"
        style={{ background: "var(--accent)" }}
      />
      <span className="flex flex-1 flex-col gap-2.5 py-4 pl-5 pr-4">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-[13.5px] font-semibold tracking-[-0.005em] text-neutral-900">
            {u.title.replace(/\s*\([^)]*\)\s*$/, "")}
          </span>
          <span
            className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: "var(--accent)" }}
          >
            {KIND_TAG[kind ?? "dwi"]}
          </span>
        </span>

        <span className="text-[11.5px] leading-relaxed text-neutral-600">{u.outcome}</span>

        <span className="mt-auto flex flex-col gap-1 pt-1 text-[10.5px] tabular-nums text-neutral-500">
          {facts ? <span>{describeFacts(facts)}</span> : null}
          {lastRun != null ? (
            <span>Last run on this machine · {formatDuration(lastRun)}</span>
          ) : null}
          {u.needs?.length ? (
            <span className="text-neutral-400">Needs {u.needs.join(" · ")}</span>
          ) : null}
        </span>
      </span>

      <span className="flex items-center justify-between gap-2 border-t border-ads-border px-5 py-2 text-[11px]">
        <span className="truncate text-neutral-400">{cardIssue ?? ""}</span>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 font-medium",
            issue && scope !== "many" && "opacity-30",
          )}
          style={{ color: "var(--accent)" }}
        >
          {scope === "many" ? "Run on many subjects" : "Run"}
          <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
        </span>
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------- panes --- */

function Pane({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-5xl px-9 py-6">{children}</div>
    </div>
  );
}

/**
 * One subject or many.
 *
 * This was a toggle inside the Pipeline tab's setup card, then a second button
 * in this heading, and neither could be found: both sat beside the analysis
 * rather than in front of it, and a reader with two hundred subjects was already
 * looking at the three cards. So the scope comes first, the cards inherit it,
 * and every control in the pane follows — a card that says "Run on many
 * subjects" cannot be mistaken for the single-study flow.
 */
function ScopeSwitch({ scope, onChange }: { scope: RunScope; onChange: (s: RunScope) => void }) {
  return (
    <span className="inline-flex rounded-md border border-ads-border bg-ads-surface p-0.5">
      {(
        [
          { id: "one" as const, label: "One subject", icon: FolderOpen },
          { id: "many" as const, label: "Many subjects", icon: Layers },
        ] as const
      ).map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          aria-pressed={scope === option.id}
          className={cn(
            "flex items-center gap-1.5 rounded px-2.5 py-1 text-[11.5px] font-medium transition",
            scope === option.id
              ? "bg-ads-accent-soft text-ads-text shadow-[inset_0_0_0_1px_var(--ads-accent-border)]"
              : "text-neutral-500 hover:text-neutral-900",
          )}
        >
          <option.icon className="h-3.5 w-3.5" />
          {option.label}
        </button>
      ))}
    </span>
  );
}

function StartPane() {
  const featured = USE_CASES.filter((u) => u.featured);
  const hasCase = useAppStore((s) => s.currentCase != null);
  const [scope, setScope] = useState<RunScope>("one");

  return (
    <Pane>
      <Intro />
      <SectionHeading
        eyebrow="Start"
        title="Run an analysis"
        blurb={
          scope === "many"
            ? "Pick a folder that holds one subdirectory per subject. They run one after another, or several at once where the machine allows it."
            : hasCase
              ? undefined
              : "Open a study and any of these can run on it."
        }
      />
      {/* In the reading path, not in a corner: the cards below run on whatever
          this says. Two previous attempts put it beside the analysis instead of
          in front of it, and neither could be found. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ScopeSwitch scope={scope} onChange={setScope} />
        <Button
          size="sm"
          variant="outline"
          onClick={() => void (scope === "many" ? pickAndOpenCohort() : pickAndOpenSubject())}
        >
          {scope === "many" ? (
            <Layers className="h-3.5 w-3.5" />
          ) : (
            <FolderOpen className="h-3.5 w-3.5" />
          )}
          {scope === "many" ? "Choose a folder of subjects…" : "Open study…"}
        </Button>
        {scope === "many" ? null : (
          <Button size="sm" variant="outline" onClick={() => void loadExampleAndOpenSubject()}>
            <FlaskConical className="h-3.5 w-3.5" />
            Load example subject
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4 sm:flex-row">
        {featured.map((u) => (
          <AnalysisCard key={u.id} u={u} scope={scope} />
        ))}
      </div>
    </Pane>
  );
}

function StudiesPane({
  recent,
  onOpen,
}: {
  recent: RecentCase[];
  onOpen: (caseJsonPath: string, workspaceId: string) => void;
}) {
  const projects = useProjects((s) => s.items);
  const refreshProjects = useProjects((s) => s.refresh);
  const openProject = useProjects((s) => s.openProject);
  const beginProject = useProjectDraft((s) => s.begin);

  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);

  return (
    <Pane>
      <SectionHeading
        eyebrow="Continue"
        title="Projects and studies"
        blurb="A project holds the question, the cohort and the protocol, and records what was run on whom. A study is one subject."
        aside={
          <div className="flex gap-1.5">
            <Button size="sm" variant="primary" onClick={beginProject}>
              <Plus className="h-3.5 w-3.5" />
              New project
            </Button>
            <Button size="sm" variant="outline" onClick={() => void pickAndOpenSubject()}>
              <FolderOpen className="h-3.5 w-3.5" />
              Open study…
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 gap-7 lg:grid-cols-2">
        <div>
          <Eyebrow className="mb-2.5">Projects</Eyebrow>
          {projects.length === 0 ? (
            <p className="rounded-xl border border-dashed border-ads-border px-4 py-7 text-center text-[11.5px] leading-relaxed text-neutral-500">
              None yet. A project reads a folder of subjects once, records who is in and why the
              rest are out, and fixes one protocol for all of them.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => void openProject(p.id)}
                  className={cn(SURFACE, "px-3.5 py-2.5 text-left transition hover:border-ads-border-strong")}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-[12.5px] font-medium text-neutral-800">
                      {p.name}
                    </span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-neutral-400">
                      {p.pipelineKind}
                    </span>
                  </span>
                  {p.question ? (
                    <span className="mt-1 block truncate text-[11px] italic text-neutral-500">
                      “{p.question}”
                    </span>
                  ) : null}
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-2.5 text-[10.5px] tabular-nums text-neutral-500">
                    <span className="text-emerald-700">{p.completed} done</span>
                    {p.offProtocol ? (
                      <span className="text-amber-700">{p.offProtocol} off protocol</span>
                    ) : null}
                    {p.failed ? <span className="text-red-700">{p.failed} failed</span> : null}
                    <span>{p.notStarted} to go</span>
                    <span className="text-neutral-400">· {p.included} in cohort</span>
                    {!p.cohortExists ? (
                      <span className="text-amber-700">· data folder missing</span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Eyebrow className="mb-2.5 flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            Recent studies
          </Eyebrow>
          {recent.length === 0 ? (
            <p className="rounded-xl border border-dashed border-ads-border px-4 py-7 text-center text-[11.5px] text-neutral-500">
              None yet.
            </p>
          ) : (
            <div className={cn(SURFACE, "overflow-hidden")}>
              {recent.slice(0, 8).map((c) => (
                <button
                  key={c.caseId}
                  type="button"
                  onClick={() => onOpen(c.caseJsonPath, "")}
                  className="group flex w-full items-center gap-3 border-b border-ads-border px-3.5 py-2.5 text-left last:border-0 hover:bg-ads-subtle"
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 shrink-0 rounded-full ring-4",
                      c.status === "completed"
                        ? "bg-emerald-500 ring-emerald-100"
                        : c.exists
                          ? "bg-neutral-300 ring-neutral-100"
                          : "bg-amber-500 ring-amber-100",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-medium text-neutral-800">
                      {c.subjectId}
                    </span>
                    <span className="block truncate text-[11px] text-neutral-500">
                      {c.studyName || c.pipelineKind.toUpperCase()} ·{" "}
                      {formatDateTime(c.lastModifiedIso)}
                    </span>
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-neutral-300 transition group-hover:translate-x-0.5 group-hover:text-neutral-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Pane>
  );
}

/**
 * The dataset review, shown in place rather than linked to. It is the same
 * screen the menu opens; a summary here that you then had to leave for the
 * real one would be the redirection this rail exists to avoid.
 */
function DatasetPane() {
  return <DatasetTab />;
}

/* ----------------------------------------------------------------- page --- */

type PlaceId = "start" | "studies" | "dataset";

const PLACES: { id: PlaceId; label: string; hint: string; icon: typeof Play }[] = [
  { id: "start", label: "Start", hint: "Run an analysis", icon: Play },
  { id: "studies", label: "Studies", hint: "Open and resume", icon: FolderOpen },
  { id: "dataset", label: "Data scan", hint: "Read a whole folder first", icon: Table2 },
];

export function HomePage() {
  const [place, setPlace] = useState<PlaceId>("start");
  const [recent, setRecent] = useState<RecentCase[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const touch = useWorkspaces((s) => s.touch);
  const activateLeaf = useNavStore((s) => s.activateLeaf);
  const openProject = useProjects((s) => s.open);

  const reload = () => {
    void listRecentCases()
      .then(setRecent)
      .catch(() => setRecent([]));
  };
  useEffect(reload, []);

  const guide = useMemo(() => buildGuide(), []);
  const hits = useMemo(() => searchGuide(guide, query), [guide, query]);

  const openWorkspace = async (caseJsonPath: string, id: string) => {
    setError(null);
    try {
      const manifest = await openCase(caseJsonPath);
      if (id) touch(id);
      showCaseInViewer(manifest);
    } catch (e) {
      setError(splitError(e).message);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-ads-bg">
      <HeaderStrip query={query} setQuery={setQuery} />
      <NewProject />

      <div className="flex min-h-0 flex-1">
        {/* ------------------------------------------------------ rail --- */}
        <nav className="flex w-[196px] shrink-0 flex-col border-r border-ads-border bg-ads-surface px-3 py-4">
          {PLACES.map((p) => {
            const on = p.id === place;
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlace(p.id)}
                className={cn(
                  "mb-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition",
                  on ? "bg-ads-accent-soft" : "hover:bg-ads-hover",
                )}
              >
                <Icon
                  className={cn("h-4 w-4 shrink-0", on ? "text-ads-accent" : "text-neutral-400")}
                />
                <span className="min-w-0">
                  <span
                    className={cn(
                      "block text-[12.5px] font-medium leading-tight",
                      on ? "text-ads-accent" : "text-neutral-800",
                    )}
                  >
                    {p.label}
                  </span>
                  <span className="block truncate text-[10.5px] leading-tight text-neutral-400">
                    {p.hint}
                  </span>
                </span>
              </button>
            );
          })}

          <span className="flex-1" />
          {error ? (
            <p className="rounded-lg bg-red-50 px-2.5 py-2 text-[10.5px] leading-snug text-red-700">
              {error}
            </p>
          ) : null}
          <p className="px-2.5 text-[10px] leading-relaxed text-neutral-400">
            OpenADS · DAGMNet · SynthStrip · ANTs · JHU-ICBM. For research and review, not a
            diagnosis.
          </p>
        </nav>

        {/* --------------------------------------------------- content --- */}
        <div className="relative min-w-0 flex-1">
          {place === "start" ? <StartPane /> : null}
          {place === "studies" ? (
            openProject ? (
              <ProjectPage project={openProject} />
            ) : (
              <StudiesPane recent={recent} onOpen={openWorkspace} />
            )
          ) : null}
          {place === "dataset" ? <DatasetPane /> : null}

          {/* Search floats over whichever place is open: looking something up
              should not cost you your place. */}
          {query.trim() ? (
            <div className="absolute inset-x-0 top-0 z-10 px-9 pt-4">
              <div className={cn(SURFACE, "mx-auto max-w-5xl overflow-hidden shadow-lg")}>
                {hits.length === 0 ? (
                  <p className="px-3 py-5 text-center text-[12px] text-neutral-500">
                    Nothing matches “{query.trim()}”.
                  </p>
                ) : (
                  hits.map(({ section, group, entry }) => {
                    const accent = accentFor(section.id);
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        disabled={entry.state === "future"}
                        style={accentVars(accent)}
                        onClick={() => {
                          setQuery("");
                          activateLeaf(entry.leaf);
                        }}
                        className="flex w-full items-baseline gap-3 border-b border-ads-border px-3.5 py-2.5 text-left last:border-0 hover:bg-[color:var(--accent-soft)] disabled:opacity-50"
                      >
                        <span className="flex w-[160px] shrink-0 items-center gap-1.5">
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ background: "var(--accent)" }}
                          />
                          <span className="truncate text-[10.5px] text-neutral-400">
                            {section.label} › {group.label}
                          </span>
                        </span>
                        <span className="shrink-0 text-[12px] font-medium text-neutral-800">
                          {entry.label}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[11px] text-neutral-500">
                          {entry.detail}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
